/**
 * Lookup objects for price-summary ids, view axes, and presentational tokens.
 * Pair with the derived types in `./types`.
 */

export const Perspectives = {
  Guest: "guest",
  Host: "host",
} as const;

export const Stages = {
  Checkout: "checkout",
  Offer: "offer",
  PostCheckout: "postCheckout",
  PreCheckout: "preCheckout",
} as const;

export const PaymentPlans = {
  PayAtVenue: "payAtVenue",
  PayInFull: "payInFull",
  SplitPayment: "splitPayment",
  ZeroDown: "zeroDown",
} as const;

/** Terminal guest booking states that adjust Section 1 totals. */
export const TerminalBookingStates = {
  Cancelled: "cancelled",
  Declined: "declined",
  Expired: "expired",
  Withdrawn: "withdrawn",
} as const;

export const SectionIds = {
  CollectedAtVenue: "collectedAtVenue",
  HostEarnings: "hostEarnings",
  PaymentPayout: "paymentPayout",
  PriceBreakdown: "priceBreakdown",
} as const;

export const GroupNames = {
  Charges: "charges",
  CollectedAtVenue: "collectedAtVenue",
  Earnings: "earnings",
  Fees: "fees",
  PaymentSchedule: "paymentSchedule",
  Payout: "payout",
} as const;

/** Declaration order for groups within a section. */
export const GROUP_ORDER = [
  GroupNames.Charges,
  GroupNames.Fees,
  GroupNames.Earnings,
  GroupNames.Payout,
  GroupNames.PaymentSchedule,
  GroupNames.CollectedAtVenue,
] as const;

export const Emphases = {
  Default: "default",
  Strong: "strong",
} as const;

export const RowColors = {
  Default: "default",
  Highlight: "highlight",
} as const;

/**
 * Shared offset vocabulary — same values as DS `PriceLineItem` `leftOffset`:
 * `none` | `left` | `indicator`.
 */
export const RowOffsets = {
  Indicator: "indicator",
  Left: "left",
  None: "none",
} as const;

/**
 * Row id registry. Display order is `ui/`, not this object.
 * Rules Sheet variants are separate RowIds (mutually exclusive `show`), not
 * branches inside one row — keeps copy, tooltips, and test IDs one-per-row.
 */
export const RowIds = {
  AddOnsTotal: "AddOnsTotal",
  AdditionalPaymentsTotal: "AdditionalPaymentsTotal",
  BookingTotal: "BookingTotal",
  CleaningFee: "CleaningFee",
  CollectedAddOnsTotal: "CollectedAddOnsTotal",
  CollectedFoodDrinkMinimum: "CollectedFoodDrinkMinimum",
  CollectedGuestSpendAtVenue: "CollectedGuestSpendAtVenue",
  Credits: "Credits",
  DayRateDiscount: "DayRateDiscount",
  DayRateDiscountReadout: "DayRateDiscountReadout",
  DifferenceDueToday: "DifferenceDueToday",
  DiySpaceRental: "DiySpaceRental",
  DiySpaceRentalRate: "DiySpaceRentalRate",
  DueAtVenue: "DueAtVenue",
  /** Guest PAV collapsed Due at venue (lives in Section 1). */
  DueAtVenueInline: "DueAtVenueInline",
  DueToday: "DueToday",
  EarningsSubtotal: "EarningsSubtotal",
  Entitlement: "Entitlement",
  EstimatedEarnings: "EstimatedEarnings",
  FoodDrinkMinimum: "FoodDrinkMinimum",
  FsvSpaceRental: "FsvSpaceRental",
  GuestSpendAtVenue: "GuestSpendAtVenue",
  HostFee: "HostFee",
  HostInlineServiceFee: "HostInlineServiceFee",
  PaidToDate: "PaidToDate",
  PaymentAmount: "PaymentAmount",
  PayoutFromPeerspace: "PayoutFromPeerspace",
  ProcessingFee: "ProcessingFee",
  Refund: "Refund",
  SpaceRentalReadout: "SpaceRentalReadout",
  SplitPaymentFee: "SplitPaymentFee",
  StaffGratuity: "StaffGratuity",
  Taxes: "Taxes",
  TerminalBookingOffset: "TerminalBookingOffset",
  UpcomingCharge: "UpcomingCharge",
  VatTaxes: "VatTaxes",
} as const;

/** Bindings map these to defineMessages. */
export const LabelIds = {
  AddOnDetails: "AddOnDetails",
  AddOnsTotal: "AddOnsTotal",
  AddOnUnitPerHour: "AddOnUnitPerHour",
  AddOnUnitPerItem: "AddOnUnitPerItem",
  AddOnUnitPerPerson: "AddOnUnitPerPerson",
  AddOnUnitPrice: "AddOnUnitPrice",
  AddOnUnitPriceOnly: "AddOnUnitPriceOnly",
  AdditionalPaymentsTotal: "AdditionalPaymentsTotal",
  AlreadyPaid: "AlreadyPaid",
  AttendeeRange: "AttendeeRange",
  AttendeeRangeReadout: "AttendeeRangeReadout",
  BeforeGratuity: "BeforeGratuity",
  CalculatedAtCheckout: "CalculatedAtCheckout",
  CleaningFee: "CleaningFee",
  CollectedAddOnsTotal: "CollectedAddOnsTotal",
  Credits: "Credits",
  DayRateDiscount: "DayRateDiscount",
  DifferenceDueToday: "DifferenceDueToday",
  DiySpaceRental: "DiySpaceRental",
  DiySpaceRentalRate: "DiySpaceRentalRate",
  DueAtVenue: "DueAtVenue",
  DueToday: "DueToday",
  Earnings: "Earnings",
  EarningsSubtotal: "EarningsSubtotal",
  Entitlement: "Entitlement",
  EntitlementCaption: "EntitlementCaption",
  EstimatedEarnings: "EstimatedEarnings",
  EventDate: "EventDate",
  FoodDrinkMinimum: "FoodDrinkMinimum",
  FoodDrinkMinimumBeforeGratuity: "FoodDrinkMinimumBeforeGratuity",
  FoodDrinkMinimumBeforeGratuityGb: "FoodDrinkMinimumBeforeGratuityGb",
  FsvSpaceRental: "FsvSpaceRental",
  GuestSpendAtVenue: "GuestSpendAtVenue",
  GuestTotal: "GuestTotal",
  HostBasePricingLink: "HostBasePricingLink",
  HourlyDiscount: "HourlyDiscount",
  HourlyPrice: "HourlyPrice",
  Included: "Included",
  IncludesDueAtVenue: "IncludesDueAtVenue",
  /** Booking total label while an offer/update is pending. */
  NewTotal: "NewTotal",
  OutsideBaseGuestMessage: "OutsideBaseGuestMessage",
  OutsideBaseHostMessage: "OutsideBaseHostMessage",
  PaidToDate: "PaidToDate",
  PaymentAmount: "PaymentAmount",
  PayoutFromPeerspace: "PayoutFromPeerspace",
  PeerspaceReservationFee: "PeerspaceReservationFee",
  PrepaidByGuest: "PrepaidByGuest",
  ProcessingFee: "ProcessingFee",
  Refund: "Refund",
  RoomTypeHours: "RoomTypeHours",
  SeeBasePricingLink: "SeeBasePricingLink",
  ServiceFee: "ServiceFee",
  SpaceRental: "SpaceRental",
  SplitPaymentFee: "SplitPaymentFee",
  StaffGratuityPercent: "StaffGratuityPercent",
  Taxes: "Taxes",
  /** “Booking {state}” offset row before total (cancelled / withdrawn / …). */
  TerminalBooking: "TerminalBooking",
  Total: "Total",
  TotalBeforeTaxes: "TotalBeforeTaxes",
  UpcomingCharge: "UpcomingCharge",
  UpcomingChargeDate: "UpcomingChargeDate",
  VatTaxes: "VatTaxes",
} as const;

export const TooltipIds = {
  AddOnsTotal: "AddOnsTotal",
  CleaningFee: "CleaningFee",
  /** Section 2 DIY host platform fee ("Service fee"). */
  DiyHostFee: "DiyHostFee",
  DiyHostVatTaxes: "DiyHostVatTaxes",
  DiySpaceRental: "DiySpaceRental",
  /** Section 2 earnings subtotal (host). */
  EarningsSubtotal: "EarningsSubtotal",
  FoodDrinkMinimum: "FoodDrinkMinimum",
  /** Section 2 FSV host platform fee ("Peerspace reservation fee"). */
  FsvHostFee: "FsvHostFee",
  FsvHostVatTaxes: "FsvHostVatTaxes",
  GuestVatTaxes: "GuestVatTaxes",
  /** Guest inline host fee (Section 1) — shared DIY/FSV copy. */
  HostFee: "HostFee",
  ProcessingFee: "ProcessingFee",
  SplitPaymentFee: "SplitPaymentFee",
  StaffGratuity: "StaffGratuity",
} as const;

/**
 * Help Center articles linked from a tooltip's explainer text, as the
 * `<id>-<slug>` path the Help Center publishes. The binding localizes the URL
 * with the viewer's locale via `getSupportArticleUrl`.
 */
export const TOOLTIP_SUPPORT_ARTICLES = {
  [TooltipIds.ProcessingFee]:
    "10119157-what-is-the-peerspace-processing-fee-for-guests",
} as const;

/** Mobile tray / web tooltip-modal detail ids — starter set. */
export const DetailIds = {
  AddOnsTotal: "AddOnsTotal",
} as const;
